<template>
  <div>
    <States v-if="checkin.aasmState === 'approved'" :checkin="checkin" class="mb-3" />
    <span v-if="stEnabled && checkin.serviceTitanJob" class="service-titan d-flex mb-2 gap-2">
      <img :src="require('mv/service_titan_logo.svg')" alt="ServiceTitan">
      ServiceTitan Job
    </span>
    <div v-if="checkin.business && checkin.business.disconnected" class="d-flex gap-2 align-items-baseline text-warning mb-3">
      <i class="far fa-exclamation-circle" />
      <p class="m-0" v-if="business">
        <span class="text-warning">Publishing to GMB is currently unavailable. No connection to profile. For more information, see the</span>
        <a :href="`/locations/${business.locationId}`" class="text-primary">Business Profile</a>.
      </p>
    </div>
    <div v-if="checkin.instagramPartlyPublished" class="d-flex gap-2 align-items-baseline text-warning mb-3">
      <i class="far fa-exclamation-circle" />
      <p class="m-0">
        <span class="text-warning">Failed to post some media on Instagram</span>
      </p>
    </div>
    <div v-if="checkin.aasmState === 'approved' && checkin.localPostObfuscatedId" class="mb-3">
      <a class="d-flex align-items-center gap-2" :href="localPostUrl(checkin)">
        <i class="far fa-link" />
        Link to GBP post
      </a>
    </div>
    <div class="mv-grid" @dragover.prevent @dragenter.prevent>
      <div class="narrow-container">
        <div class="form-field mb-3">
          <label>Business</label>
          <multiselect
            ref="businessSelect"
            v-model="business"
            :options="businessesOptions"
            :loading="businessesLoading"
            :max-height="540"
            :limit="300"
            :options-limit="300"
            :allow-empty="false"
            :disabled="isFieldDisabled()"
            :internal-search="false"
            track-by="id"
            label="name"
            placeholder="Pick a business"
            @search-change="refetchBusinesses"
            @input="fetchAssociations">
            <template #option="{ option }">
              <div :class="['text-truncate', { 'text-grey': option.$isDisabled }]">
                <span>{{ option.name }}</span>
              </div>
              <small v-if="option.disconnected" class="text-muted">GBP API connection isn't active</small>
              <span class="d-block font-size-14 text-truncate">{{ option.locationData.address }}</span>
              <span class="d-block font-size-14 text-truncate">{{ option.locationData.storeCode }}</span>
            </template>

            <template slot="afterList">
              <div v-observe-visibility="reachedEndOfBusinesses" />
            </template>
          </multiselect>
          <span v-if="fieldErrors.business" class="mt-1 small text-danger">{{ fieldErrors.business }}</span>
        </div>

        <div v-if="checkinExternalIdEnabled" ref="externalIdNode" class="form-field mb-3">
          <label>External moment ID</label>
          <input
            v-model="externalId"
            type="text"
            :disabled="isFieldDisabled()"
            placeholder="External moment ID">
          <p v-if="fieldErrors.externalId" class="mt-1 small text-danger">
            <span>This external id is already taken for another moment for this business.</span>
            <a class="d-flex align-items-center" target="_blank" :href="`/gb/moments/${fieldErrors.externalId}/edit`">
              Moment with this external id
              <i class="pl-2 fa-regular fa-arrow-up-right-from-square" />
            </a>
          </p>
        </div>

        <AddressControl
          ref="addressFormFieldNode"
          :checkin="checkin"
          :formatted-address="formattedAddress"
          :address-source="addressSource"
          :st-enabled="stEnabled"
          :show-map="showMap"
          :is-address-not-selected="isAddressNotSelected"
          :google-map-api-key="googleMapApiKey"
          :is-field-disabled="checkin.aasmState === 'rejected'"
          @input="onChangeAddressSource" />

        <div class="form-field mb-4">
          <label class="mb-0" for="description">
            Text<span class="text-danger">*</span>
          </label>
          <textarea v-model="description"
            required="true"
            :maxlength="descriptionMaxLength"
            rows="4"
            id="description"
            placeholder="Describe in detail your work"
            :disabled="isFieldDisabled()" />
        </div>
      </div>
      <div class="sub-container">
        <MomentMap v-if="showMap && coords" :coords="coords" :google-map-api-key="googleMapApiKey" />
        <AddressOutput
          :address-source="addressSource"
          :address-components="addressComponents"
          :checkin="checkin"
          :is-address-edited="isAddressEdited"
          :from-office="fromOffice" />
      </div>
    </div>
    <MomentImages
      class="mb-4"
      :business="business"
      :images="images"
      :disabled="isFieldDisabled() || isRequestSent || checkin.aasmState === 'rejected'"
      @setImages="setImages" />

    <MomentVideo :class="['form-field mb-4', { 'mb-5': checkin.publishVideo }]"
      :checkin="checkin"
      :disabled="isFieldDisabled() || isRequestSent"
      @setVideoUrls="setVideoUrls" />
    <div class="mv-grid">
      <div class="narrow-container">
        <div class="form-field mb-3">
          <AppCheckbox v-model="publishVideo" :disabled="isFieldDisabled() || business?.$isDisabled">
            Publish videos to GBP
            <i id="video-hint" class="far fa-exclamation-circle text-primary" />
            <BTooltip target="video-hint">
              <div class="text-left">
                Videos attached to the moment will be published in GBP as separate media entities.
                <br>
                Please note: Google might review videos up to 48 hours.
              </div>
            </BTooltip>
          </AppCheckbox>
        </div>

        <div class="form-field mb-3">
          <label>Customer<span class="text-danger">*</span></label>
          <input
            v-model="customer"
            type="text"
            :disabled="checkin.aasmState === 'rejected'"
            placeholder="Customer's name">
        </div>

        <div class="form-field mb-3">
          <label>Categories</label>
          <multiselect
            v-model="category"
            :options="categories"
            :loading="categoriesLoading"
            :disabled="checkin.aasmState === 'rejected'"
            :max-height="540"
            :limit="300"
            :options-limit="300"
            track-by="id"
            label="title"
            placeholder="Pick a category" />
        </div>

        <div class="form-field mb-4">
          <label>Employee</label>
          <multiselect
            v-model="employee"
            :options="employees"
            :loading="employeesLoading"
            :max-height="540"
            :limit="300"
            :options-limit="300"
            :disabled="checkin.aasmState !== 'pending'"
            track-by="id"
            label="name"
            placeholder="Pick a employee" />
        </div>

        <div class="form-field">
          <div class="row">
            <label class="col col-sm-2 col-form-label">Schedule</label>

            <div class="col-12 col-sm-8 col-lg-6 pt-2">
              <div class="switch-button switch-button-success">
                <input type="checkbox"
                  id="schedule_moment"
                  v-model="isScheduled"
                  :disabled="isFieldDisabled()">
                <span>
                  <label for="schedule_moment" />
                </span>
              </div>
            </div>
          </div>

          <transition name="slide-animation">
            <div class="row" v-show="isScheduled">
              <label class="col-12 col-sm-2 col-form-label">Schedule for</label>

              <div class="col-12 col-sm-5 col-lg-4">
                <flat-pickr
                  v-model="scheduledAt"
                  class="form-control"
                  :config="flatpickrConfig"
                  :disabled="isFieldDisabled()" />
              </div>

              <label class="col-12 col-sm-2 col-lg-6 col-form-label"> {{ business?.timeZone }} </label>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <MomentDestinations
      v-if="business"
      :can-edit-destinations="!isFieldDisabled()"
      :selected-business="business"
      :checkin="checkin"
      :fb-enabled="fbEnabled"
      :ig-enabled="igEnabled"
      @updateMomentDestinations="destinations = $event" />
    <CtaButtons
      v-if="destinations.publishGmb !== undefined ? destinations.publishGmb : checkin.publishGmb"
      @updateCtaType="updateCtaType"
      @updateCtaLink="updateCtaLink"
      :checkin="checkin"
      :disabled="isFieldDisabled() || isRequestSent"
      :cta-type="ctaType"
      :cta-link="ctaLink" />
    <MomentReviews
      v-if="business"
      :checkin-created-at="checkin?.createdAt"
      :selected-business-id="business.id"
      :gmb-review="gmbReview"
      :free-form-review="freeFormReview"
      :review-kind="reviewKind"
      @changeReviewKind="reviewKind = $event"
      @selectGmbReview="selectGmbReview"
      @selectFreeFormReview="selectFreeFormReview" />
    <div class="mv-grid">
      <div class="narrow-container d-flex pt-5 pb-6">
        <button
          v-if="isSaveButtonAllowed()"
          class="btn rounded mr-3"
          :disabled="sendingMoment || checkin.aasmState === 'rejected'"
          @click="submitCheckin">
          Save
          <template v-if="checkin.aasmState !== 'approved'">
            Draft
          </template>
        </button>

        <button
          v-if="checkin.aasmState === 'pending' && !moderationRequired"
          class="btn btn-primary rounded mr-3"
          type="button"
          @click.prevent="publish"
          :disabled="disableFormButtons">
          Save and Publish
        </button>

        <button
          v-if="checkin.obfuscatedId && checkin.aasmState === 'pending' && !moderationRequired"
          class="btn btn-outline-danger rounded mr-3"
          type="button"
          @click.prevent="reject">
          Reject
        </button>

        <button
          class="btn rounded"
          type="button"
          @click.prevent="cancel"
          :disabled="isRequestSent">
          Cancel
        </button>

        <button
          v-if="isDeleteButtonAllowed()"
          class="btn btn-outline-danger rounded ml-auto"
          type="button"
          @click.prevent="destroy"
          :disabled="isRequestSent">
          <i class="far fa-trash" />
          <span class='ml-2'>Delete</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import snakeCaseKeys from 'snakecase-keys'
import camelcaseKeys from 'camelcase-keys-deep'
import FlatPickr from 'vue-flatpickr-component'

import { FLATPICKR_WEEKDAYS_MAP } from 'common/constants'
import AppCheckbox from '../../../components/checkbox'
import MomentMap from './map'
import MomentVideo from './video'
import AddressOutput from './address_output'
import AddressControl from './address_control'
import States from './states'
import MomentReviews from './reviews'
import MomentDestinations from './moment_destinations'
import MomentImages from './images'
import CtaButtons from './cta.vue'

import { isValidUrl, isEmpty } from '../../../../common/helpers'
import { ensureGoogleMap } from '../../../../common/map_helpers'
import { runSwal } from '../../../../common/delete_with_swal'
import {
  BASE_URL,
  handleError,
  MAX_FILES,
  REVIEW_KINDS,
  ADDRESS_SOURCES,
  fetchBusinesses,
  fetchBusiness,
  fetchCategoriesForSelect,
  fetchEmployeesForSelect
} from '../helpers'

const retrieveFoundAddressFromCheckin = (checkin) => {
  let foundAddress = null

  const { addressComponents, formattedAddress, coords } = checkin
  if (formattedAddress) {
    foundAddress = { addressComponents, formattedAddress, ...coords }
  }

  return foundAddress
}

const BUSINESSES_PER_PAGE = 10

const IN_PROGRESS = {
  IMAGE_UPLOADING: 'Image uploading is still in progress. If you submit a moment right now the images wont be added.',
  DESCRIPTIONS_GENERATION: 'Image description generation is still in progress. If you submit a moment right now the descriptions wont be added to images.'
}

const confirmInProgressSubmit = async(text) => swal({
  text,
  icon: 'warning',
  buttons: {
    cancel: {
      visible: true,
      text: 'Ok, wait',
      value: false,
      className: 'btn btn-success'
    },
    confirm: {
      text: 'Submit anyway',
      value: true,
      className: 'btn btn-danger'
    }
  },
  dangerMode: true
})

export default {
  components: {
    Multiselect,
    AppCheckbox,
    MomentMap,
    MomentImages,
    MomentVideo,
    MomentReviews,
    MomentDestinations,
    AddressOutput,
    AddressControl,
    FlatPickr,
    States,
    CtaButtons
  },
  props: {
    checkin: Object,
    ajaxCheckinsPath: String,
    ajaxCheckinImagePath: String,
    googleMapApiKey: String,
    igEnabled: Boolean,
    fbEnabled: Boolean,
    checkinExternalIdEnabled: Boolean,
    moderationRequired: Boolean,
    stEnabled: { type: Boolean, default: false }
  },
  data() {
    const { review } = this.checkin
    let reviewKind = REVIEW_KINDS.OFF
    let gmbReview = null
    let freeFormReview = null
    if (review) {
      if (review.remoteId) {
        gmbReview = review
        reviewKind = REVIEW_KINDS.GMB
      } else {
        reviewKind = REVIEW_KINDS.FREE_FORM
        freeFormReview = review
      }
    }

    return {
      formattedAddress: this.checkin.formattedAddress,
      addressComponents: this.checkin.addressComponents,
      description: this.checkin.description || '',
      scheduledAt: null,
      fromOffice: this.checkin.fromOffice,
      customer: this.checkin.customer || '',
      externalId: this.checkin.externalId || '',
      ctaType: this.checkin.ctaType,
      ctaLink: this.checkin.ctaLink || '',
      category: this.checkin.category,
      employee: this.checkin.employee,
      images: this.checkin.images,
      publishVideo: this.checkin.publishVideo || false,
      videoUrls: this.checkin.videoUrls || [],
      review: review || undefined,
      employees: [],
      employeesLoading: false,
      categories: [],
      categoriesLoading: false,
      destinations: {},
      coords: null,
      marker: null,
      onDragItem: null,
      isRequestSent: false,
      isScheduled: Boolean(this.checkin.scheduledAt),
      showMap: false,
      foundAddress: retrieveFoundAddressFromCheckin(this.checkin),
      isAddressNotSelected: false,
      isAddressEdited: false,
      fieldErrors: {},
      sendingMoment: false,
      addressSource: this.checkin.addressSource.toUpperCase(),

      gmbReview,
      freeFormReview,
      reviewKind,
      businesses: [],
      businessesTotalPages: 1,
      businessesCurrentPage: 1,
      business: this.checkin.business ? { ...this.checkin.business } : null,
      businessesLoading: true
    }
  },
  created() {
    this.scheduledAt = this.checkin.scheduledAt
    this.MAX_FILES = MAX_FILES

    fetchBusinesses({ businessesCurrentPage: this.businessesCurrentPage, per_page: BUSINESSES_PER_PAGE })
      .then(async({ businesses, totalPages }) => {
        this.businessesLoading = false
        this.businessesTotalPages = totalPages
        this.businesses = businesses
        await this.setBusiness()
        this.setCoordinates()
        this.fetchAssociations(false)
      })

    this.flatpickrConfig = {
      enableTime: true,
      dateFormat: 'd/m/Y H:i',
      minDate: 'today',
      locale: {
        firstDayOfWeek: FLATPICKR_WEEKDAYS_MAP[Styxie.USER.beginningOfWeek]
      }
    }
  },
  mounted() {
    ensureGoogleMap({ googleMapApiKey: this.googleMapApiKey }, () => {
      this.showMap = true
    })

    this.handlePresetFields()
  },
  computed: {
    descriptionMaxLength() {
      if (this.checkin.publishGmb) {
        return 1500
      }
      if (this.checkin.publishInstagram) {
        return 2200
      }

      return null
    },
    businessesOptions() {
      return this.businesses.map((business) => ({
        ...business,
        $isDisabled: !business.active
      }))
    },
    disableFormButtons() {
      return this.isFieldDisabled() || this.isRequestSent
    },
    isScheduledInThePast() {
      if (!this.checkin.scheduledAt) return false
      const DATE_MASK = 'DD/MM/YYYY HH:mm Z'

      const scheduledAt = moment(this.checkin.scheduledAt, DATE_MASK).toDate().getTime()
      const now = new Date().getTime()
      return scheduledAt < now
    },
    isMomentPublished() {
      const isMomentApproved = this.checkin.aasmState === 'approved'
      return isMomentApproved && (!this.checkin.scheduledAt || this.isScheduledInThePast)
    }
  },
  methods: {
    refetchBusinesses(search) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.businesses = []
        this.businessesCurrentPage = 1
        this.businessesLoading = true
        fetchBusinesses({
          page: this.businessesCurrentPage,
          search: search,
          per_page: BUSINESSES_PER_PAGE
        }).then(({ businesses, totalPages }) => {
          this.businessesLoading = false
          this.businessesTotalPages = totalPages
          this.businesses = businesses
        })
      }, 500)
    },
    reachedEndOfBusinesses(isVisible) {
      if (!isVisible) return

      if (this.businessesCurrentPage < this.businessesTotalPages) {
        this.businessesCurrentPage++
        this.businessesLoading = true

        fetchBusinesses({
          page: this.businessesCurrentPage,
          search: this.$refs.businessSelect.search,
          per_page: BUSINESSES_PER_PAGE
        }).then(({ businesses, totalPages }) => {
          this.businessesLoading = false
          this.businessesTotalPages = totalPages
          this.businesses = [...this.businesses, ...businesses]
        })
      }
    },
    handlePresetFields() {
      const search = qs.parse(window.location.search, { ignoreQueryPrefix: true })
      const {
        business: businessId,
        date
      } = search

      if (businessId) {
        const relatedBusiness = this.businesses.find((business) => business.id === businessId)
        if (relatedBusiness) {
          this.business = relatedBusiness
        }
      }

      if (date) {
        this.isScheduled = true
        const DATE_MASK = 'DD/MM/YYYY HH:mm'

        this.scheduledAt = moment(date)
          .set({ hour: 12, minute: 0 })
          .format(DATE_MASK)
      }
    },
    updateCtaType(newValue) {
      this.ctaType = newValue
    },
    updateCtaLink(value) {
      this.ctaLink = value
    },
    isFieldDisabled(fieldName = null) {
      const isNotPending = this.checkin.aasmState !== 'pending'
      if (this.moderationRequired && this.checkin.obfuscatedId &&
        (isNotPending || !this.checkin.currentUserCreator)) return true

      const neverDisabled = new Set('review', 'customer', 'categories')
      if (neverDisabled.has(fieldName)) return false
      if (this.isMomentPublished) return true

      return false
    },
    async setBusiness() {
      if (this.checkin.business) return

      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      const businessId = urlParams.get('business_id')

      if (businessId) {
        const tryBusiness = this.businesses.find((bus) => bus.id === businessId)

        if (tryBusiness) {
          this.business = tryBusiness
        } else {
          this.business = await fetchBusiness(businessId)
        }

        return
      }

      this.business = this.businessesOptions.find((business) => !business.$isDisabled) || null
    },
    setCoordinates() {
      try {
        if (this.addressSource === ADDRESS_SOURCES.FROM_OFFICE) {
          this.coords = this.business.coords
        } else if (this.addressSource === ADDRESS_SOURCES.GOOGLE_AUTOCOMPLETE) {
          this.coords = { lat: this.foundAddress.lat, lng: this.foundAddress.lng }
        } else if (this.addressSource === ADDRESS_SOURCES.FROM_ST_JOB) {
          this.coords = this.checkin.serviceTitanJob.coords
        } else if (this.addressSource === ADDRESS_SOURCES.PUBLIC_API_ADDRESS) {
          this.coords = this.checkin.coords
        }
      } catch {
        this.coords = { lat: null, lng: null }
      }
    },
    isCoordsValid(coords) {
      return coords?.lat && coords?.lng
    },
    fetchAssociations(reset = true) {
      if (!this.business) return

      if (reset) {
        this.categories = []
        this.category = {}
        this.employees = []
        this.employee = {}
      }

      this.setCoordinates()

      this.categoriesLoading = true
      fetchCategoriesForSelect({ businessId: this.business.id })
        .then((categories) => {
          this.categoriesLoading = false
          this.categories = categories
        })

      this.employeesLoading = true
      fetchEmployeesForSelect({ businessId: this.business.id })
        .then((employees) => {
          this.employeesLoading = false
          this.employees = employees.map(({ id, fullName }) => ({ id, name: fullName }))
        })
    },
    setImages(images) {
      this.images = images
    },
    setVideoUrls(urls) {
      this.videoUrls = urls
    },
    updateAddress(foundAddress) {
      this.isAddressEdited = true
      this.isAddressNotSelected = false
      this.foundAddress = foundAddress
      this.setCoordinates()
      if (foundAddress) {
        const { addressComponents, formattedAddress } = foundAddress
        this.addressComponents = camelcaseKeys(addressComponents)
        this.formattedAddress = formattedAddress
      } else {
        this.addressComponents = null
        this.formattedAddress = ''
      }
    },
    isDeleteButtonAllowed() {
      if (!this.checkin.obfuscatedId) return false
      if (!this.moderationRequired) return true
      const isPending = this.checkin.aasmState === 'pending'

      return this.checkin.currentUserCreator && isPending
    },
    isSaveButtonAllowed() {
      const isPending = this.checkin.aasmState === 'pending'

      return !this.checkin.obfuscatedId || !this.moderationRequired || isPending && this.checkin.currentUserCreator
    },
    selectGmbReview(gmbReview) {
      this.gmbReview = gmbReview
    },
    selectFreeFormReview(freeFormReview) {
      this.freeFormReview = freeFormReview
    },
    async submitCheckin(additionalAction = '') {
      /* TODO: this is kinda fix for 422 responce from WLC user (linda user) */
      const validateErrors = []

      if (!this.description) {
        validateErrors.push('Description can\'t be blank')
      }

      if (!this.customer) {
        validateErrors.push('Customer can\'t be blank')
      }

      if (!['action_type_unspecified', 'call'].includes(this.ctaType) && !this.ctaLink) {
        validateErrors.push('CTA button link can\'t be blank')
      }

      if (this.ctaLink && !isValidUrl(this.ctaLink)) {
        validateErrors.push('CTA button link is not valid')
      }

      validateErrors.forEach((errorText) => {
        toastr.error(errorText)
      })

      if (validateErrors.length) return

      if (this.images.filter(({ uploading }) => uploading).length) {
        const result = await confirmInProgressSubmit(IN_PROGRESS.IMAGE_UPLOADING)

        if (!result) return
      }

      if (this.images.filter(({ isDescriptionGenerating }) => isDescriptionGenerating).length) {
        const result = await confirmInProgressSubmit(IN_PROGRESS.DESCRIPTIONS_GENERATION)

        if (!result) return
      }

      this.isRequestSent = true
      this.fieldErrors = {}
      const formData = new FormData()

      formData.append('checkin[address_source]', this.addressSource.toLowerCase())
      if (this.addressSource === ADDRESS_SOURCES.FROM_OFFICE) {
        formData.append('checkin[formatted_address]', '')
      } else if (this.addressSource === ADDRESS_SOURCES.FROM_ST_JOB) {
        formData.append('checkin[formatted_address]', '')
      } else if (this.addressSource === ADDRESS_SOURCES.GOOGLE_AUTOCOMPLETE) {
        if (this.addressComponents && Array.isArray(this.addressComponents)) {
          formData.append('checkin[formatted_address]', this.formattedAddress)
          this.addressComponents.forEach((row) => {
            formData.append('checkin[address_components][][short_name]', row.shortName)
            formData.append('checkin[address_components][][long_name]', row.longName)

            row.types?.forEach((type) => {
              formData.append('checkin[address_components][][types][]', type)
            })
          })
        } else {
          this.isAddressNotSelected = true
          this.$refs.addressFormFieldNode.$el.scrollIntoView({
            behavior: 'smooth'
          })
          this.isRequestSent = false
          return
        }
      }

      if (!this.business) {
        this.fieldErrors.business = 'This field cannot be empty'
        this.isRequestSent = false
        return
      }
      formData.append('checkin[cta_type]', this.ctaType)
      formData.append('checkin[cta_link]', this.ctaLink)
      formData.append('checkin[from_office]', this.fromOffice)
      formData.append('checkin[description]', this.description)
      formData.append('checkin[mobile_business_id]', this.business.id)
      formData.append('checkin[external_id]', this.externalId?.trim())
      formData.append('checkin[customer]', this.customer)
      formData.append('checkin[coords][lat]', this.coords.lat)
      formData.append('checkin[coords][lng]', this.coords.lng)
      formData.append('checkin[publish_video]', this.publishVideo)
      formData.append('checkin[scheduled_at]', this.isScheduled ? this.scheduledAt : null)
      formData.append('checkin[employee_id]', this.employee?.id)

      if (this.reviewKind === REVIEW_KINDS.OFF || this.reviewKind === REVIEW_KINDS.GMB) {
        [
          'review_remote_id',
          'free_form_reviewer',
          'free_form_comment',
          'free_form_reply',
          'free_form_platform',
          'free_form_link'
        ].forEach((key) => {
          formData.append(`checkin[${key}]`, '')
        })
      }

      if (this.reviewKind === REVIEW_KINDS.GMB) {
        formData.append('checkin[review_remote_id]', this.gmbReview.remoteId)
      }

      if (this.reviewKind === REVIEW_KINDS.FREE_FORM) {
        formData.append('checkin[review_remote_id]', '')
        formData.append('checkin[free_form_star_rating]', this.freeFormReview.rating)
        formData.append('checkin[free_form_reviewer]', this.freeFormReview.reviewer)
        formData.append('checkin[free_form_comment]', this.freeFormReview.comment)
        formData.append('checkin[free_form_reply]', this.freeFormReview.reviewReply)
        formData.append('checkin[free_form_platform]', this.freeFormReview.freeFormPlatform)
        formData.append('checkin[free_form_link]', this.freeFormReview.freeFormLink)
      }

      const snakecasedDestinations = snakeCaseKeys(this.destinations)
      Object.keys(snakecasedDestinations).forEach((destinationName) => {
        formData.append(`checkin[${destinationName}]`, snakecasedDestinations[destinationName])
      })

      if (this.videoUrls.length) {
        this.videoUrls.forEach((row) => formData.append('checkin[video_urls][]', row))
      }

      if (!isEmpty(this.category)) {
        formData.append('checkin[mobile_category_id]', this.category.id)
      }

      if (!isEmpty(this.employee)) {
        formData.append('checkin[employee_id]', this.employee.id)
      }

      this.images.forEach((image) => {
        if (image.id) {
          const imageDescription = image.description ? image.description : ''
          formData.append('images[][id]', image.id)
          formData.append('images[][description]', imageDescription)
          if (image.collageBefore) formData.append('images[][collage_before]', true)
          if (image.collageAfter) formData.append('images[][collage_after]', true)
        }
      })

      if (additionalAction === 'publish') {
        formData.append('publish', true)
      }

      const headers = { headers: { 'Content-Type': 'multipart/form-data' } }

      if (this.sendingMoment) return
      this.sendingMoment = true

      if (this.checkin.obfuscatedId) {
        await this.updateCheckin(formData, headers)
      } else {
        await this.createCheckin(formData, headers)
      }

      this.sendingMoment = false
    },
    updateCheckin(data, headers) {
      return axios.patch(
        `/gb/ajax/moments/${this.checkin.obfuscatedId}`,
        data,
        headers
      ).then(() => {
        $(document).one('turbolinks:load', () => {
          toastr.success('Moment updated successfully')
        })
        Turbolinks.visit(BASE_URL)
      }).catch((err) => {
        this.isRequestSent = false
        this.handleCheckinError(err)
      })
    },
    createCheckin(data, headers) {
      return axios.post(
        this.ajaxCheckinsPath,
        data,
        headers
      ).then(() => {
        $(document).one('turbolinks:load', () => {
          toastr.success('Moment created successfully')
        })
        Turbolinks.visit(BASE_URL)
      }).catch((err) => {
        this.isRequestSent = false
        this.handleCheckinError(err)
      })
    },
    handleCheckinError(error) {
      const NO_CITY_ERROR = 'City can\'t be blank'

      if (error.response.status === 422) {
        const { errors, additional_data: additionalData } = error.response.data
        const isNoCityError = errors.find((errorString) => errorString === NO_CITY_ERROR)

        if (isNoCityError) {
          toastr.error('The selected address doesn\'t belong to any city. Please try another one.')
          return
        }

        const isExternalIdError = additionalData?.field === 'external_id'
        if (isExternalIdError) {
          this.fieldErrors.externalId = additionalData.existing_moment_id
          this.$refs.externalIdNode?.scrollIntoView({
            behavior: 'smooth'
          })
          return
        }
      }

      handleError(error)
    },
    publish() {
      this.submitCheckin('publish')
    },
    reject() {
      this.isRequestSent = true
      axios.post(
        `/gb/ajax/moments/${this.checkin.obfuscatedId}/reject`,
        {},
        {}
      ).then(() => {
        $(document).one('turbolinks:load', () => {
          toastr.success('Moment rejected successfully')
        })
        Turbolinks.visit(BASE_URL)
      }).catch((err) => {
        this.isRequestSent = false
        handleError(err)
      })
    },
    cancel() {
      window.location = BASE_URL
    },
    destroy() {
      runSwal(() => {
        this.isRequestSent = true
        axios.delete(`/gb/ajax/moments/${this.checkin.obfuscatedId}`).then((res) => {
          $(document).one('turbolinks:load', () => {
            toastr.success(res.data.message)
          })
          Turbolinks.visit(BASE_URL)
        }).catch(() => {
          toastr.error('Something went wrong')
          this.isRequestSent = false
        })
      })
    },
    localPostUrl(checkin) {
      return `/gmb/locations/${checkin.business.gmbLocationRemoteId}/local_posts/${checkin.localPostObfuscatedId}/edit`
    },
    onChangeAddressSource(payload) {
      this.addressSource = ADDRESS_SOURCES[payload.source]

      if (this.addressSource === ADDRESS_SOURCES.GOOGLE_AUTOCOMPLETE) {
        this.updateAddress(payload.foundAddress)
      } else {
        this.setCoordinates()
      }
    }
  },
  watch: {
    business(val) {
      if (val) {
        this.employee = {}

        if (this.checkin.business && val.id === this.checkin.business.id) {
          this.publishVideo = this.checkin.publishVideo
        } else {
          this.publishVideo = val.publishVideoByDefault
        }
      }
    }
  }
}
</script>
